import { useState, type ChangeEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Upload, Link2, Download, Eye } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import {
  EVIDENCE_MEDIA_MIME_GROUPS,
  FileLibraryPickerDialog,
  FileSourceChooser,
  fileMatchesEvidenceMediaAccept,
} from '@/features/file-library';
import type { FileLibrarySelection, FileSourceMode } from '@/features/file-library/types';
import { useFileLibraryFeatureEnabled } from '@/features/file-library/useFileLibraryFeature';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import {
  useAttachEvidenceFromLibrary,
  useEvidenceList,
  useUploadEvidence,
  useLinkEvidence,
} from './useEvidence';
import { EVIDENCE_MEDIA_ACCEPT, evidenceDownloadUrl } from './api';
import {
  evidenceTypeValues,
  linkRequestSchema,
  targetRecordTypeValues,
  type EvidenceFileDto,
  type EvidenceType,
  type LinkRequestInput,
  type TargetRecordType,
} from './types';

const NS = 'evidence';

// Image content the browser can render inline (mirrors the backend's inline
// content-disposition list in evidence/routes.rs). The download endpoint
// serves these as `inline` and authenticates via the session cookie, so an
// <img src> loads them directly — no blob fetch needed.
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'];
const isImageFile = (fileName: string | null | undefined): boolean => {
  if (!fileName) return false;
  const ext = fileName.split('.').pop()?.toLowerCase();
  return !!ext && IMAGE_EXTENSIONS.includes(ext);
};

const UploadDialog = ({
  batchId,
  open,
  onOpenChange,
  libraryAvailable,
}: {
  batchId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  libraryAvailable: boolean;
}) => {
  const { t } = useTranslation(NS);
  const upload = useUploadEvidence();
  const attachFromLibrary = useAttachEvidenceFromLibrary();
  const [file, setFile] = useState<File | null>(null);
  const [sourceMode, setSourceMode] = useState<FileSourceMode>('local');
  const [libraryFile, setLibraryFile] = useState<FileLibrarySelection | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [type, setType] = useState<EvidenceType>('photo');
  const [error, setError] = useState<string | null>(null);
  const effectiveSourceMode = libraryAvailable ? sourceMode : 'local';
  const isSubmitting = upload.isPending || attachFromLibrary.isPending;

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setError(null);
  };

  const onSubmit = async () => {
    if (effectiveSourceMode === 'local' && !file) {
      setError(t('efs.evidence.validation.file_required'));
      return;
    }
    if (effectiveSourceMode === 'library' && !libraryFile) {
      setError(t('efs.file_library.validation.file_required', { ns: 'fileLibrary' }));
      return;
    }
    try {
      if (effectiveSourceMode === 'library' && libraryFile) {
        await attachFromLibrary.mutateAsync({
          batchId,
          companyFileId: libraryFile.company_file_id,
          evidenceType: type,
        });
      } else if (file) {
        await upload.mutateAsync({ batchId, file, evidence_type: type });
      }
      onOpenChange(false);
      setFile(null);
      setError(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'upload failed';
      setError(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.evidence.sections.upload')}</DialogTitle>
          <DialogDescription>{t('efs.evidence.subtitle')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {libraryAvailable ? (
            <FileSourceChooser
              value={effectiveSourceMode}
              onChange={(mode) => {
                setSourceMode(mode);
                setError(null);
              }}
              disabled={isSubmitting}
            />
          ) : null}
          <div>
            <label className="text-sm font-medium">{t('efs.evidence.fields.evidence_type')}</label>
            <Select value={type} onValueChange={(v) => setType(v as EvidenceType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {evidenceTypeValues.map((v) => (
                  <SelectItem key={v} value={v}>
                    {t(`efs.evidence.types.${v}`, { defaultValue: v })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {effectiveSourceMode === 'local' ? (
            <div>
              <label className="text-sm font-medium">
                {t('efs.evidence.fields.original_file_name')}
              </label>
              <Input type="file" accept={EVIDENCE_MEDIA_ACCEPT} onChange={onFile} />
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPickerOpen(true)}
                disabled={isSubmitting}
              >
                {t('efs.file_library.picker.choose', { ns: 'fileLibrary' })}
              </Button>
              {libraryFile ? (
                <div className="rounded-md border border-efs-border bg-efs-bg-subtle p-3 text-sm">
                  <div className="break-all font-medium">{libraryFile.display_name}</div>
                  <div className="mt-1 font-mono text-xs text-efs-text-muted">
                    {libraryFile.sha256.slice(0, 24)}
                  </div>
                </div>
              ) : null}
            </div>
          )}
          {error ? <p className="text-sm text-efs-error">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-1 h-4 w-4" />
            )}
            {isSubmitting ? t('efs.evidence.actions.uploading') : t('efs.evidence.actions.upload')}
          </Button>
        </DialogFooter>
        {libraryAvailable ? (
          <FileLibraryPickerDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            onSelect={(selectedFile) => {
              setLibraryFile(selectedFile);
              setError(null);
            }}
            batchId={batchId}
            acceptMimeGroups={EVIDENCE_MEDIA_MIME_GROUPS}
            acceptFile={fileMatchesEvidenceMediaAccept}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const LinkDialog = ({
  row,
  open,
  onOpenChange,
}: {
  row: EvidenceFileDto | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const { t } = useTranslation(NS);
  const link = useLinkEvidence();

  const form = useForm<LinkRequestInput>({
    resolver: zodResolver(linkRequestSchema),
    defaultValues: {
      target_record_type:
        (row?.target_record_type as TargetRecordType | undefined) ?? 'inbound_lot',
      target_record_id: row?.target_record_id ?? '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    if (!row) return;
    await link.mutateAsync({ evidenceFileId: row.evidence_file_id, body: values });
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.evidence.sections.link')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-3" noValidate>
            <FormField
              control={form.control}
              name="target_record_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.evidence.fields.target_record_type')}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {targetRecordTypeValues.map((v) => (
                          <SelectItem key={v} value={v}>
                            {t(`efs.evidence.target_types.${v}`, { defaultValue: v })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="target_record_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.evidence.fields.target_record_id')}</FormLabel>
                  <FormControl>
                    <Input placeholder="uuid" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={link.isPending}
              >
                {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
              </Button>
              <Button type="submit" disabled={link.isPending}>
                {link.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Link2 className="mr-1 h-4 w-4" />
                )}
                {link.isPending
                  ? t('efs.evidence.actions.linking')
                  : t('efs.evidence.actions.link')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export const EvidencePage = () => {
  const { t } = useTranslation([NS, 'common']);
  const { batchId } = useParams();
  const user = useCurrentUser();

  const [evidenceTypeFilter, setEvidenceTypeFilter] = useState<EvidenceType | 'all'>('all');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkRow, setLinkRow] = useState<EvidenceFileDto | null>(null);
  const [previewRow, setPreviewRow] = useState<EvidenceFileDto | null>(null);

  const filter: { evidence_type?: EvidenceType } = {};
  if (evidenceTypeFilter !== 'all') filter.evidence_type = evidenceTypeFilter;

  const list = useEvidenceList(batchId, filter);
  const fileLibraryEnabled = useFileLibraryFeatureEnabled();

  const canUpload = hasPermission(user, 'evidence.manage');
  const canUseFileLibrary =
    fileLibraryEnabled &&
    (hasPermission(user, 'file_library.read') || hasPermission(user, 'file_library.manage'));

  if (!batchId) {
    return (
      <PageHeader
        title={t('efs.evidence.title')}
        subtitle={t('efs.common.messages.no_batch_selected', {
          ns: 'common',
          defaultValue: 'バッチが選択されていません。',
        })}
      />
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.evidence.title')}
        subtitle={t('efs.evidence.subtitle')}
        actions={
          canUpload ? (
            <Button onClick={() => setUploadOpen(true)}>
              <Upload className="mr-1 h-4 w-4" />
              {t('efs.evidence.actions.upload')}
            </Button>
          ) : null
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('efs.evidence.sections.list')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-sm">{t('efs.evidence.actions.filter')}:</label>
            <Select
              value={evidenceTypeFilter}
              onValueChange={(v) => setEvidenceTypeFilter(v as EvidenceType | 'all')}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('efs.evidence.actions.all_types')}</SelectItem>
                {evidenceTypeValues.map((v) => (
                  <SelectItem key={v} value={v}>
                    {t(`efs.evidence.types.${v}`, { defaultValue: v })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {list.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (list.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-efs-text-muted">{t('efs.evidence.messages.no_files')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t('efs.evidence.fields.preview')}</TableHead>
                  <TableHead>{t('efs.evidence.fields.evidence_type')}</TableHead>
                  <TableHead>{t('efs.evidence.fields.original_file_name')}</TableHead>
                  <TableHead>{t('efs.evidence.fields.target_record_type')}</TableHead>
                  <TableHead>{t('efs.evidence.fields.uploaded_at')}</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(list.data ?? []).map((row) => {
                  const image = isImageFile(row.original_file_name);
                  return (
                    <TableRow key={row.evidence_file_id}>
                      <TableCell>
                        {image ? (
                          <button
                            type="button"
                            onClick={() => setPreviewRow(row)}
                            className="block h-12 w-12 overflow-hidden rounded border border-efs-border"
                            aria-label={t('efs.evidence.actions.preview')}
                          >
                            <img
                              src={evidenceDownloadUrl(row.evidence_file_id)}
                              alt={row.original_file_name}
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          </button>
                        ) : (
                          <span className="text-efs-text-muted">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {t(`efs.evidence.types.${row.evidence_type}`, {
                          defaultValue: row.evidence_type,
                        })}
                      </TableCell>
                      <TableCell>{row.original_file_name}</TableCell>
                      <TableCell>
                        {row.target_record_type
                          ? t(`efs.evidence.target_types.${row.target_record_type}`, {
                              defaultValue: row.target_record_type,
                            })
                          : '-'}
                      </TableCell>
                      <TableCell>{row.uploaded_at}</TableCell>
                      <TableCell className="space-x-1">
                        {image ? (
                          <Button variant="ghost" size="sm" onClick={() => setPreviewRow(row)}>
                            <Eye className="mr-1 h-3 w-3" />
                            {t('efs.evidence.actions.preview')}
                          </Button>
                        ) : null}
                        <Button asChild variant="ghost" size="sm">
                          <a
                            href={evidenceDownloadUrl(row.evidence_file_id)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Download className="mr-1 h-3 w-3" />
                            {t('efs.evidence.actions.download')}
                          </a>
                        </Button>
                        {canUpload ? (
                          <Button variant="ghost" size="sm" onClick={() => setLinkRow(row)}>
                            <Link2 className="mr-1 h-3 w-3" />
                            {t('efs.evidence.actions.link')}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <UploadDialog
        batchId={batchId}
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        libraryAvailable={canUseFileLibrary}
      />
      <LinkDialog row={linkRow} open={!!linkRow} onOpenChange={(v) => !v && setLinkRow(null)} />

      {/* Lightbox: full-size image preview (reuses the shadcn Dialog). */}
      <Dialog open={!!previewRow} onOpenChange={(v) => !v && setPreviewRow(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewRow?.original_file_name}</DialogTitle>
          </DialogHeader>
          {previewRow ? (
            <img
              src={evidenceDownloadUrl(previewRow.evidence_file_id)}
              alt={previewRow.original_file_name}
              className="max-h-[75vh] w-full object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvidencePage;
